// /src/components/data-display/PoolsTopTenHashPieTile.jsx

import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import ApexCharts from 'apexcharts';
import { DataTableHeader } from './DataTableHeader';
import { DataTableFooter } from './DataTableFooter';
import { ThemedFlex } from '../../theme/ThemedFlex'
import { Select, Box, useColorMode } from '@chakra-ui/react';
import { CustomSpinner } from './CustomSpinner';
import { ChartWrapper } from '../charts/ChartWrapper';

const getBreakpoint = (width) => {
 if (width < 768) return 'base';
 if (width >= 768 && width < 992) return 'md';
 if (width >= 992 && width < 1200) return 'lg';
 return 'xl';
};

const useBreakpoint = () => {
 const [breakpoint, setBreakpoint] = useState(() => getBreakpoint(window.innerWidth));

 useEffect(() => {
   const handleResize = () => setBreakpoint(getBreakpoint(window.innerWidth));
   window.addEventListener('resize', handleResize);
   return () => window.removeEventListener('resize', handleResize);
 }, []);

 return breakpoint;
};

export const PoolsTopTenHashPieTile = ({
 title,
 subtitle,
 icon,
 link,
 linkLabel = 'Learn More',
 data,
 uniqueChartId,
 formatLargeNumber
}) => {
 const [chartData, setChartData] = useState([]);
 const chartRef = useRef(null);
 const chartInstance = useRef(null);
 const resizeHandlerRef = useRef(null);
 const { colorMode } = useColorMode();
 const borderColor = colorMode === 'dark' ? '#121212' : '#ffffff';
 const [timeframe, setTimeframe] = useState('7d');

 const handleTimeframeChange = (event) => {
   setTimeframe(event.target.value);
 };

 const processChartData = useMemo(() => {
   if (!data || !data.ts05 || !data.ts05.hashrate_pools) return [];
   
   const days = {
     '7d': 7, '3m': 90, '6m': 180, '1y': 365, '2y': 730, '3y': 1095,
   };
 
   const now = new Date();
   const daysAgo = new Date(now.getTime() - (days[timeframe] * 24 * 60 * 60 * 1000));
   const daysAgoTime = daysAgo.getTime();
 
   // Single pass to filter and aggregate
   const poolShares = {};
   for (const pool of data.ts05.hashrate_pools) {
     if (pool.timestamp * 1000 >= daysAgoTime) {
       if (!poolShares[pool.poolName]) {
         poolShares[pool.poolName] = { totalShare: 0, count: 0 };
       }
       poolShares[pool.poolName].totalShare += pool.share;
       poolShares[pool.poolName].count += 1;
     }
   }

   // Convert to array and calculate averages
   const poolArray = [];
   for (const [name, data] of Object.entries(poolShares)) {
     poolArray.push([name, (data.totalShare / data.count) * 100]);
   }
   
   // Sort and get top 10
   poolArray.sort((a, b) => b[1] - a[1]);
   const top10 = poolArray.slice(0, 10);
   
   // Calculate "Others" percentage
   let top10Total = 0;
   const processedData = [];
   for (const pool of top10) {
     top10Total += pool[1];
     processedData.push({ y: pool[1], x: pool[0] });
   }
   
   const othersPercentage = Math.max(0, 100 - top10Total);
   if (othersPercentage > 0.1) {
     processedData.push({ y: othersPercentage, x: 'Others' });
   }
 
   return processedData;
 }, [data, timeframe]);
 
 useEffect(() => {
   setChartData(processChartData);
 }, [processChartData]);

 const breakpoint = useBreakpoint();

 const options = useMemo(() => {
   if (chartData.length === 0) return null;
   
   return {
     chart: {
       fontFamily: 'Ubuntu, sans-serif',
       type: 'pie',
       height: '100%',
       width: '100%',
       toolbar: {
         show: false,
       },
     },
     labels: chartData.map(data => data.x),
     series: chartData.map(data => data.y),
     colors: ["#f7931a", "#329239", "#0d579b", "#4d4d4d", "#7e57c2", "#ff5722", "#03a9f4", "#ffc107", "#9c27b0", "#795548", "#9e9e9e"],
     legend: {
       labels: {
         colors: colorMode === 'dark' ? '#999' : '#333',
       },
       show: breakpoint === 'md' || breakpoint === 'lg' || breakpoint === 'xl',
     },
     dataLabels: {
       enabled: false,
     },
     stroke: {
       colors: [borderColor],
     },
     tooltip: {
      theme: colorMode,
      y: {
        formatter: (value) => value.toFixed(2) + "%",
      },
    },
   };
 }, [chartData, borderColor, breakpoint, colorMode]);

 // Memoized resize handler
 const handleResize = useCallback(() => {
   if (chartInstance.current) {
     chartInstance.current.updateOptions({
       chart: {
         width: '100%',
         height: '100%',
       },
     }, false, false, false);
   }
 }, []);
 
 useEffect(() => {
   if (options && chartRef.current) {
     // Clean up previous instance if timeframe changed
     if (chartInstance.current) {
       chartInstance.current.destroy();
       chartInstance.current = null;
     }
     
     // Create new instance
     chartInstance.current = new ApexCharts(chartRef.current, options);
     chartInstance.current.render();
     
     // Store resize handler reference
     resizeHandlerRef.current = handleResize;
     window.addEventListener('resize', handleResize);

     return () => {
       window.removeEventListener('resize', handleResize);
       if (chartInstance.current) {
         chartInstance.current.destroy();
         chartInstance.current = null;
       }
     };
   }
 }, [options, handleResize]);

 if (!data || !data.ts05 || !data.ts05.hashrate_pools) {
   return (
     <ThemedFlex
       direction="column"
       mb="12px"
       py="18px"
       px="20px"
       flex={1}
       borderRadius={3}
       style={{ minHeight: '337px', maxHeight: '100%' }}
     >
       <DataTableHeader title={title} subtitle={subtitle} icon={icon} />
       <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center">
         <CustomSpinner />
       </Box>
     </ThemedFlex>
   );
 }

 return (
   <ThemedFlex
     direction="column"
     mb="12px"
     py="18px"
     px="20px"
     flex={1}
     borderRadius={3}
     style={{ minHeight: '337px', maxHeight: '100%' }}
   >
     <DataTableHeader title={title} subtitle={subtitle} icon={icon} />
     <Select
  value={timeframe}
  onChange={handleTimeframeChange}
  variant="filled"
  size="xs"
  alignSelf="flex-start"
  width="100px"
  mb={2}
>
  <option value="7d">7 Days</option>
  <option value="3m">3 Months</option>
  <option value="6m">6 Months</option>
  <option value="1y">1 Year</option>
  <option value="2y">2 Years</option>
  <option value="3y">3 Years</option>
</Select>
     <ChartWrapper flexGrow={1} minHeight="337px" chartId={uniqueChartId}>
       <div ref={chartRef} className={`chart-container-${uniqueChartId}`} style={{ height: '100%' }} />
     </ChartWrapper>
     <DataTableFooter link={link} linkLabel={linkLabel} />
   </ThemedFlex>
 );
};